/* eslint-disable react-refresh/only-export-components */
import type { AppManagementSummary, BuildSummary, CatalogApp, ReleaseSummary } from "@cheesy/contracts";
import { useAuthActions } from "@convex-dev/auth/react";
import { useAction, useConvexAuth, useMutation, useQuery } from "convex/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "./api";
import { demoApps, demoNotifications, demoReleases } from "./demo";
import { useLocation } from "react-router";
import { routeDataNeeds } from "./route-data";

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: "user" | "admin";
  status: "active" | "suspended";
  access: "allowed" | "denied";
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  createdAt: number;
  read: boolean;
}
export interface WhitelistItem { id: string; email: string; role: "user" | "admin"; status: "active" | "suspended"; personalRepositoryAccess: boolean; updatedAt: number }
export interface AccountRequestItem { id: string; email: string; name: string; status: "pending" | "approved" | "denied"; submittedAt: number; updatedAt: number; decidedAt?: number; decidedBy?: string }
export interface AuditEventItem { id: string; action: string; targetType: string; targetId: string; appId?: string; actor?: string; metadataJson: string; createdAt: number }
export interface DeletedAccountResult { transferredApps: { id: string; name: string; repository?: string }[] }
export interface AppLog { timestamp: string; severity: string; message: string }

interface DataContextValue {
  mode: "demo" | "convex";
  isLoading: boolean;
  isAuthenticated: boolean;
  currentUser: CurrentUser | null;
  apps: CatalogApp[];
  notifications: NotificationItem[];
  whitelist: WhitelistItem[];
  accountRequests: AccountRequestItem[];
  pendingAccountRequestCount: number;
  releases: ReleaseSummary[];
  builds: BuildSummary[];
  management: AppManagementSummary[];
  auditEvents: AuditEventItem[];
  requestSignInCode: (email: string) => Promise<void>;
  verifySignInCode: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestAccount: (input: { name: string; email: string; turnstileToken: string }) => Promise<void>;
  decideAccountRequest: (requestId: string, decision: "approve" | "deny") => Promise<void>;
  markAllRead: () => Promise<void>;
  reviewApp: (appId: string, decision: "approve" | "reject" | "suspend", reason?: string) => Promise<void>;
  promoteRelease: (releaseId: string) => Promise<void>;
  submitForApproval: (appId: string) => Promise<void>;
  createPreview: (releaseId: string) => Promise<string>;
  createLaunch: (appId: string, returnPath?: string) => Promise<string>;
  setMobileSupported: (appId: string, mobileSupported: boolean) => Promise<void>;
  createDraft: (input: { name: string; summary: string; repository: string; branch: string }) => Promise<string>;
  upsertWhitelist: (email: string, role: "user" | "admin", status: "active" | "suspended") => Promise<void>;
  setPersonalRepositoryAccess: (email: string, allowed: boolean) => Promise<void>;
  deleteRevokedAccount: (email: string) => Promise<DeletedAccountResult>;
  writeSecret: (appId: string, name: string, value: string) => Promise<void>;
  setAppLifecycle: (appId: string, operation: "suspend" | "archive" | "restore", reason?: string) => Promise<void>;
  deleteApp: (appId: string) => Promise<void>;
  addOwner: (appId: string, email: string) => Promise<void>;
  removeOwner: (appId: string, userId: string) => Promise<void>;
  overrideFinding: (findingId: string, reason: string) => Promise<void>;
  readLogs: (appId: string, buildId?: string) => Promise<AppLog[]>;
}

const DataContext = createContext<DataContextValue | null>(null);
const initialDemoWhitelist: WhitelistItem[] = [{ id: "w1", email: "alawre1@gmail.com", role: "admin", status: "active", personalRepositoryAccess: true, updatedAt: Date.now() }];
const initialDemoAccountRequests: AccountRequestItem[] = [
  { id: "ar1", name: "Jordan Lee", email: "jordan@example.com", status: "pending", submittedAt: Date.now() - 45 * 60_000, updatedAt: Date.now() - 45 * 60_000 },
  { id: "ar2", name: "Sam Rivera", email: "sam@example.com", status: "approved", submittedAt: Date.now() - 4 * 86_400_000, updatedAt: Date.now() - 3 * 86_400_000, decidedAt: Date.now() - 3 * 86_400_000, decidedBy: "admin@team254.com" },
  { id: "ar3", name: "Taylor Morgan", email: "taylor@example.com", status: "denied", submittedAt: Date.now() - 7 * 86_400_000, updatedAt: Date.now() - 6 * 86_400_000, decidedAt: Date.now() - 6 * 86_400_000, decidedBy: "admin@team254.com" },
];

function DemoDataProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(true);
  const [apps, setApps] = useState(demoApps);
  const [notifications, setNotifications] = useState(demoNotifications);
  const [whitelist, setWhitelist] = useState(initialDemoWhitelist);
  const [accountRequests, setAccountRequests] = useState(initialDemoAccountRequests);

  const value = useMemo<DataContextValue>(() => ({
    mode: "demo",
    isLoading: false,
    isAuthenticated: authenticated,
    currentUser: authenticated ? { id: "demo-admin", name: "Demo Admin", email: "admin@team254.com", role: "admin", status: "active", access: "allowed" } : null,
    apps,
    notifications,
    whitelist,
    accountRequests,
    pendingAccountRequestCount: accountRequests.filter((request) => request.status === "pending").length,
    releases: demoReleases,
    builds: [],
    management: apps.map((app) => ({ appId: app.id, owners: app.owners.map((name, index) => ({ id: `${app.id}-${index}`, name, email: `${name.toLowerCase().replace(/\s+/g, ".")}@example.com` })), repository: app.repository ? { fullName: app.repository, branch: "main", connected: true } : undefined, secrets: [] })),
    auditEvents: [],
    requestSignInCode: async () => undefined,
    verifySignInCode: async (_email, code) => {
      if (code !== "123456") throw new Error("Invalid demo code");
      setAuthenticated(true);
    },
    signOut: async () => setAuthenticated(false),
    requestAccount: async ({ name, email }) => setAccountRequests((items) => {
      const normalized = email.trim().toLowerCase();
      if (whitelist.some((entry) => entry.email === normalized && entry.status === "active")) return items;
      const existing = items.find((item) => item.email === normalized);
      if (!existing) return [{ id: crypto.randomUUID(), name: name.trim(), email: normalized, status: "pending", submittedAt: Date.now(), updatedAt: Date.now() }, ...items];
      if (existing.status === "pending") return items.map((item) => item.id === existing.id ? { ...item, name: name.trim(), updatedAt: Date.now() } : item);
      return items.map((item) => item.id === existing.id ? { id: item.id, name: name.trim(), email: normalized, status: "pending", submittedAt: Date.now(), updatedAt: Date.now() } : item);
    }),
    decideAccountRequest: async (requestId, decision) => {
      const request = accountRequests.find((item) => item.id === requestId);
      if (!request || request.status !== "pending") throw new Error("This account request has already been decided.");
      const now = Date.now();
      setAccountRequests((items) => items.map((item) => item.id === requestId ? { ...item, status: decision === "approve" ? "approved" : "denied", decidedAt: now, decidedBy: "admin@team254.com", updatedAt: now } : item));
      if (decision === "approve") setWhitelist((items) => {
        const existing = items.find((item) => item.email === request.email);
        return existing ? items.map((item) => item.id === existing.id ? { ...item, role: "user", status: "active", personalRepositoryAccess: false, updatedAt: now } : item) : [{ id: crypto.randomUUID(), email: request.email, role: "user", status: "active", personalRepositoryAccess: false, updatedAt: now }, ...items];
      });
    },
    markAllRead: async () => setNotifications((items) => items.map((item) => ({ ...item, read: true }))),
    reviewApp: async (appId, decision) => setApps((items) => items.map((app) => app.id === appId ? { ...app, status: decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "suspended" } : app)),
    setMobileSupported: async (appId, mobileSupported) => setApps((items) => items.map((app) => app.id === appId ? { ...app, mobileSupported, updatedAt: Date.now() } : app)),
    promoteRelease: async () => undefined,
    submitForApproval: async (appId) => setApps((items) => items.map((app) => app.id === appId ? { ...app, status: "pending_approval" } : app)),
    createPreview: async () => "/demo-app?name=Candidate%20preview",
    createLaunch: async (appId) => {
      const app = apps.find((item) => item.id === appId);
      return `/demo-app?name=${encodeURIComponent(app?.name ?? "Demo app")}`;
    },
    createDraft: async ({ name, summary }) => {
      const id = crypto.randomUUID();
      setApps((items) => [...items, {
        id,
        slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        name,
        summary,
        description: summary,
        status: "draft",
        mobileSupported: false,
        categories: ["Uncategorized"],
        tags: [],
        owners: ["Demo Admin"],
        permissions: { camera: false, microphone: false, geolocation: false, clipboardRead: false, clipboardWrite: false, popups: false, fullscreen: true, userDirectory: false },
        updatedAt: Date.now(),
      }]);
      return id;
    },
    upsertWhitelist: async (email, role, status) => setWhitelist((items) => {
      const normalized = email.trim().toLowerCase(); const now = Date.now(); const existing = items.find((item) => item.email === normalized);
      return existing ? items.map((item) => item.id === existing.id ? { ...item, role, status, personalRepositoryAccess: role === "admin" && status === "active" ? item.personalRepositoryAccess : false, updatedAt: now } : item) : [{ id: crypto.randomUUID(), email: normalized, role, status, personalRepositoryAccess: false, updatedAt: now }, ...items];
    }),
    setPersonalRepositoryAccess: async (email, allowed) => setWhitelist((items) => items.map((item) => item.email === email ? { ...item, personalRepositoryAccess: allowed, updatedAt: Date.now() } : item)),
    deleteRevokedAccount: async (email) => {
      const normalized = email.trim().toLowerCase();
      const account = whitelist.find((item) => item.email === normalized);
      if (!account || account.status !== "suspended") throw new Error("Revoke this account's access before deleting it.");
      const transferredApps = apps.filter((app) => app.owners.some((name) => `${name.toLowerCase().replace(/\s+/g, ".")}@example.com` === normalized));
      setApps((items) => items.map((app) => {
        if (!transferredApps.some((affected) => affected.id === app.id)) return app;
        const retainedOwners = app.owners.filter((name) => `${name.toLowerCase().replace(/\s+/g, ".")}@example.com` !== normalized);
        return { ...app, owners: retainedOwners.includes("Demo Admin") ? retainedOwners : [...retainedOwners, "Demo Admin"] };
      }));
      setWhitelist((items) => items.filter((item) => item.email !== normalized));
      return { transferredApps: transferredApps.map((app) => ({ id: app.id, name: app.name, repository: app.repository })) };
    },
    writeSecret: async () => undefined,
    setAppLifecycle: async (appId, operation) => setApps((items) => items.map((app) => app.id === appId ? { ...app, status: operation === "restore" ? (app.liveVersion ? "approved" : "draft") : operation === "archive" ? "archived" : "suspended" } : app)),
    deleteApp: async (appId) => setApps((items) => items.filter((app) => app.id !== appId)),
    addOwner: async () => undefined,
    removeOwner: async () => undefined,
    overrideFinding: async () => undefined,
    readLogs: async () => [],
  }), [accountRequests, apps, authenticated, notifications, whitelist]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

function ConvexDataProvider({ children }: { children: ReactNode }) {
  const needs = routeDataNeeds(useLocation().pathname);
  const { signIn: authSignIn, signOut: authSignOut } = useAuthActions();
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const [profileSyncError, setProfileSyncError] = useState<string | null>(null);
  const profileSyncStarted = useRef(false);
  const me = useQuery(api.users.me, {});
  const hasAppAccess = me?.access === "allowed" && me.status === "active";
  const loadAdmin = hasAppAccess && me?.role === "admin" && needs.admin;
  const apps = useQuery(api.apps.listVisible, hasAppAccess ? { limit: 50 } : "skip");
  const notifications = useQuery(api.notifications.listMine, hasAppAccess ? { limit: 50 } : "skip");
  const whitelistResult = useQuery(api.users.listWhitelist, loadAdmin ? { paginationOpts: { numItems: 50, cursor: null } } : "skip");
  const pendingRequests = useQuery(api.accountRequests.list, loadAdmin ? { status: "pending", paginationOpts: { numItems: 50, cursor: null } } : "skip");
  const approvedRequests = useQuery(api.accountRequests.list, loadAdmin ? { status: "approved", paginationOpts: { numItems: 50, cursor: null } } : "skip");
  const deniedRequests = useQuery(api.accountRequests.list, loadAdmin ? { status: "denied", paginationOpts: { numItems: 50, cursor: null } } : "skip");
  const pendingAccountRequestCount = useQuery(api.accountRequests.countPending, loadAdmin ? {} : "skip");
  const releases = useQuery(api.releases.listMine, hasAppAccess && needs.releases ? { limit: 20 } : "skip");
  const builds = useQuery(api.builds.listMine, hasAppAccess && needs.releases ? { limit: 100 } : "skip");
  const management = useQuery(api.apps.listManagement, hasAppAccess && needs.management ? { limit: 50 } : "skip");
  const auditResult = useQuery(api.admin.listAuditEvents, loadAdmin ? { paginationOpts: { numItems: 25, cursor: null } } : "skip");
  const sync = useMutation(api.users.syncCurrent);
  const submitAccountRequest = useAction(api.accountRequests.submit);
  const decideRequest = useMutation(api.accountRequests.decide);
  const markRead = useMutation(api.notifications.markAllRead);
  const review = useMutation(api.apps.review);
  const saveMobileSupport = useMutation(api.apps.setMobileSupported);
  const create = useMutation(api.apps.createDraft);
  const submitApproval = useMutation(api.apps.submitForApproval);
  const promote = useAction(api.deployments.promote);
  const launch = useMutation(api.launch.create);
  const preview = useMutation(api.launch.createPreview);
  const saveWhitelist = useMutation(api.users.upsertWhitelist);
  const savePersonalRepositoryAccess = useMutation(api.users.setPersonalRepositoryAccess);
  const deleteAccount = useMutation(api.users.deleteRevokedAccount);
  const secretGrant = useMutation(api.secrets.createWriteGrant);
  const lifecycle = useMutation(api.apps.setLifecycle);
  const removeApp = useAction(api.appDeletion.remove);
  const saveOwner = useMutation(api.apps.addOwner);
  const dropOwner = useMutation(api.apps.removeOwner);
  const overrideScan = useMutation(api.builds.overrideFinding);
  const fetchLogs = useAction(api.builds.readLogs);
  const createLaunch = useCallback(async (appId: string, returnPath?: string) => (await launch({ appId: appId as never, returnPath })).launchUrl, [launch]);

  useEffect(() => {
    if (!isAuthenticated || me !== null || profileSyncStarted.current || profileSyncError) return;
    profileSyncStarted.current = true;
    void sync({})
      .catch((error: unknown) => setProfileSyncError(error instanceof Error ? error.message : "Profile setup failed."))
      .finally(() => { profileSyncStarted.current = false; });
  }, [isAuthenticated, me, profileSyncError, sync]);

  const value: DataContextValue = {
    mode: "convex",
    isLoading: authLoading || (isAuthenticated && (me === undefined || (me === null && !profileSyncError))) || (hasAppAccess && (
      apps === undefined ||
      (needs.management && management === undefined) ||
      (needs.releases && (releases === undefined || builds === undefined)) ||
      (loadAdmin && [whitelistResult, pendingRequests, approvedRequests, deniedRequests, pendingAccountRequestCount, auditResult].some((value) => value === undefined))
    )),
    isAuthenticated,
    currentUser: (me as CurrentUser | null | undefined) ?? null,
    apps: (apps ?? []) as CatalogApp[],
    notifications: (notifications ?? []) as NotificationItem[],
    whitelist: (whitelistResult?.page ?? []) as WhitelistItem[],
    accountRequests: [...(pendingRequests?.page ?? []), ...(approvedRequests?.page ?? []), ...(deniedRequests?.page ?? [])] as AccountRequestItem[],
    pendingAccountRequestCount: pendingAccountRequestCount ?? 0,
    releases: (releases ?? []) as ReleaseSummary[],
    builds: (builds ?? []) as BuildSummary[],
    management: (management ?? []) as AppManagementSummary[],
    auditEvents: (auditResult?.page ?? []) as AuditEventItem[],
    requestSignInCode: async (email) => { await authSignIn("email", { email }); },
    verifySignInCode: async (email, code) => { await authSignIn("email", { email, code }); },
    signOut: async () => { setProfileSyncError(null); profileSyncStarted.current = false; await authSignOut(); },
    requestAccount: async (input) => { await submitAccountRequest(input); },
    decideAccountRequest: async (requestId, decision) => { await decideRequest({ requestId: requestId as never, decision }); },
    markAllRead: async () => { await markRead({}); },
    reviewApp: async (appId, decision, reason) => { await review({ appId: appId as never, decision, reason }); },
    setMobileSupported: async (appId, mobileSupported) => { await saveMobileSupport({ appId: appId as never, mobileSupported }); },
    promoteRelease: async (releaseId) => { await promote({ releaseId: releaseId as never }); },
    submitForApproval: async (appId) => { await submitApproval({ appId: appId as never }); },
    createPreview: async (releaseId) => (await preview({ releaseId: releaseId as never })).launchUrl,
    createLaunch,
    createDraft: async (input) => String(await create(input)),
    upsertWhitelist: async (email, role, status) => { await saveWhitelist({ email, role, status }); },
    setPersonalRepositoryAccess: async (email, allowed) => { await savePersonalRepositoryAccess({ email, allowed }); },
    deleteRevokedAccount: async (email) => await deleteAccount({ email }),
    writeSecret: async (appId, name, value) => { const grant = await secretGrant({ appId: appId as never, name }); const response = await fetch(`${grant.controllerUrl}/secrets/write`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token: grant.token, value }) }); if (!response.ok) throw new Error(await response.text()); },
    setAppLifecycle: async (appId, operation, reason) => { await lifecycle({ appId: appId as never, operation, reason }); },
    deleteApp: async (appId) => { await removeApp({ appId: appId as never }); },
    addOwner: async (appId, email) => { await saveOwner({ appId: appId as never, email }); },
    removeOwner: async (appId, userId) => { await dropOwner({ appId: appId as never, userId: userId as never }); },
    overrideFinding: async (findingId, reason) => { await overrideScan({ findingId: findingId as never, reason }); },
    readLogs: async (appId, buildId) => await fetchLogs({ appId, buildId }),
  };
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function AppDataProvider({ configured, children }: { configured: boolean; children: ReactNode }) {
  return configured ? <ConvexDataProvider>{children}</ConvexDataProvider> : <DemoDataProvider>{children}</DemoDataProvider>;
}

export function useAppData(): DataContextValue {
  const value = useContext(DataContext);
  if (!value) throw new Error("useAppData must be used inside AppDataProvider");
  return value;
}
